import React from 'react';
import PropTypes from 'prop-types';

import Form from 'common/components/form';
import {connectToForm} from 'common/components/form/utils';
import _UploadModal from 'common/components/upload-modal';
import {t} from 'common/utils/translation-util';

export const UPLOAD_POINT_DATA_FORM_ID = 'UPLOAD_POINT_DATA_FORM';

export default class UploadPointDataModal extends React.Component {
  static propTypes = {
    customizeFormUrl: PropTypes.string,
    forwardRef: PropTypes.object,
    learnUrl: PropTypes.string,
    unitsUrl: PropTypes.string,
  };

  makeSteps() {
    const {learnUrl, unitsUrl, customizeFormUrl} = this.props;

    const learnExportLink = (
      <a href={learnUrl} rel='noopener noreferrer' target='_blank'>
        {t(' Learn how to export data')}
      </a>
    );

    const unitsLink = (
      <a href={unitsUrl} rel='noopener noreferrer' target='_blank'>
        {t(' Download the list of accepted units here.')}
      </a>
    );

    return [
      {
        title: t('Customize the CSV template for your data'),
        contents: (
          <Form formId={UPLOAD_POINT_DATA_FORM_ID} url={customizeFormUrl} />
        ),
      },
      {
        title: t('Download and complete CSV template'),
        isDownloadStep: true,
        subtext: (
          <>
            <b>{t(`Instructions`)}</b>
            {':'}
            <br />
            <ul>
              <li>
                {t(`
              If the data you upload overlaps with existing data in Building
              Insights, the existing data will be overwritten. It is recommended that
              you export existing data before overwriting it.
            `)}
                {learnExportLink}
              </li>
              <li>
                {t(`
              Unless you've selected the template for 'Multiple point types' all
              points in the CSV template must have the same point type, reading type,
              and unit.
            `)}
              </li>
              <li>
                {t(`
              All points are identified by their Atrius ID, located on
              each point's Profile.
            `)}
              </li>
              <li>
                {t(`
              Timestamps must be in the building’s local time zone.
            `)}
              </li>
              <li>
                {t(`
              Timestamps must be in chronological order (oldest reading at top,
              newest reading at bottom) and consecutive. If there are any gaps
              between timestamps, then those gaps may override any existing data.
            `)}
              </li>
              <li>
                {t(`
              CSV uploads for Multiple point types can be formatted in two ways:
            `)}
                <ul>
                  <li>
                    {t(`
                  Source unit / Source reading type selections allow you to include
                  data from multiple point types in a single file, so long as the
                  data entered matches the source unit and reading type for each
                  point (set on the point profile page). Column headers identify
                  points by their Atrius IDs.
                `)}
                  </li>
                  <li>
                    {t(`
                  Custom unit / Custom reading type selections allow you to enter
                  data from multiple point types in any unit or reading type. Column
                  headers must include Atrius ID, unit, and reading type
                  in the following format:
                `)}
                    <ul>
                      <li>{'"Atrius ID (unit,reading type)"'}</li>
                      <li>
                        {t(`
                      Units: Full names and abbreviations are accepted.
                    `)}
                        {unitsLink}
                      </li>
                      <li>
                        {t(`Reading types: `)}
                        <br />
                        {'Interval consumption'}
                        <br />
                        {'Interval demand, Totalizer'}
                        <br />
                        {'Bi-Directional totalizer'}
                        <br />
                        {'Instantaneous measurement'}
                        <br />
                        {'Daily consumption'}
                        <br />
                        {'Daily demand'}
                        <br />
                        {'Monthly consumption'}
                        <br />
                        {'Monthly demand'}
                        <br />
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>
                {t(`The following date formats are supported`)}
                {':'}
                <br />
                {'MM-DD-YYYY'}
                <br />
                {'MM/DD/YYYY'}
                <br />
                {'MM.DD.YYYY'}
                <br />
                {'DD-MM-YYYY'}
                <br />
                {'DD/MM/YYYY'}
                <br />
                {'DD.MM.YYYY'}
                <br />
              </li>
              <li>
                {t(`The following time formats are supported`)}
                {':'}
                <br />
                {'09:30:00'}
                <br />
                {'09:30'}
                <br />
                {'9:30'}
                <br />
                {'09:30:00am'}
                <br />
                {'09:30am'}
                <br />
                {'9:30am'}
                <br />
                {'09:30:00 am'}
                <br />
                {'09:30 am'}
                <br />
                {'9:30 am'}
                <br />
                {'21:30:00'}
                <br />
                {'21:30'}
                <br />
              </li>
            </ul>
          </>
        ),
      },
      {
        title: t('Upload completed CSV template'),
        subtext: (
          <>
            <b>{t('Proceed carefully')}</b>
            {'.'}
            {t(`
              If the data you upload overlaps with existing data in Building
              Insights, the existing data will be overwritten. It is recommended that
              you export existing data before overwriting it.
            `)}
            {learnExportLink}
          </>
        ),
      },
    ];
  }

  render() {
    const {forwardRef, ...otherProps} = this.props;
    const steps = this.makeSteps();
    const UploadModal = connectToForm(_UploadModal);
    return (
      <UploadModal
        formId={UPLOAD_POINT_DATA_FORM_ID}
        ref={forwardRef}
        steps={steps}
        {...otherProps}
      />
    );
  }
}
